'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTechnology } from '@/hooks/technology';
import type { ISerialPort } from '@/hooks/useSerialPort';
import { fetchWithAuth } from '@/lib/api';
import { diffObjects, filterIrrelevantDiffs } from '@/lib/utils/diff';
import type { IConfigurationLog } from '@/types/production/configuration-log';
import type { IConfigurationProfile } from '@/types/production/configuration-profile';
import type { Device } from '@/types/production/device';
import type { ITechnology } from '@/types/production/tool';

namespace Namespace {
  export interface UseCheckConfigurationProps {
    technology: ITechnology | null;
    configurationProfile?: IConfigurationProfile | null;
    configurationLog?: IConfigurationLog[] | null;
    autoChecking?: boolean;
  }

  export interface Detected {
    port: ISerialPort;
    equipment?: Equipment | undefined;
    status: 'fully_identified' | 'partially_identified' | 'not_identified';
  }

  interface Equipment {
    imei?: string | undefined;
    iccid?: string | undefined;
    firmware?: string | undefined;
    serial?: string | undefined;
    lora_keys?: Partial<Device['Equipment']['lora_keys']>;
  }

  export interface Checked {
    checked: boolean;
    checked_at: Date;
    port: ISerialPort;
    equipment: Required<Pick<Equipment, 'serial' | 'firmware'>> & Equipment;
    config: IConfigurationProfile['config'];
    messages?: any[];
    configurationProfileId?: string;
    configurationLogId?: string;
  }
}

// Helper function to transform lora_keys from frontend format to backend format
function transformLoraKeys(
  lora_keys?: Partial<Device['Equipment']['lora_keys']>,
): IConfigurationLog['equipment']['lora_keys'] {
  if (!lora_keys) return undefined;

  return {
    dev_eui: lora_keys.device_eui,
    app_eui: lora_keys.application_eui,
    app_key: lora_keys.application_key,
    dev_addr: lora_keys.device_address,
    nwk_s_key: lora_keys.network_session_key,
    app_s_key: lora_keys.application_session_key,
  };
}

// Helper function to transform equipment
function transformEquipment(equipment: {
  serial: string;
  firmware: string;
  imei?: string;
  iccid?: string;
  lora_keys?: Partial<Device['Equipment']['lora_keys']>;
}): IConfigurationLog['equipment'] {
  return {
    serial: equipment.serial,
    firmware: equipment.firmware,
    imei: equipment.imei,
    iccid: equipment.iccid,
    lora_keys: transformLoraKeys(equipment.lora_keys),
  };
}

/**
 * Filter out IP port differences for NB2 DNS mode.
 * When IP is 0.0.0.0 (DNS mode), the device returns the DNS port in the IP response.
 * We want to ignore this difference since it's expected behavior.
 */
function filterNb2DnsModeDiffs(diffs: any[], desired: Record<string, any>): any[] {
  // Check if we're in DNS mode (IP is 0.0.0.0)
  const isInDnsMode = (key: string) => {
    const ip = desired[key];
    return ip && typeof ip === 'object' && ip.ip === '0.0.0.0';
  };

  return diffs.filter((diff) => {
    // Ignore ip_primary.port and ip_secondary.port differences when in DNS mode
    if (
      (diff.key === 'ip_primary.port' || diff.key === 'ip_secondary.port') &&
      isInDnsMode(diff.key.split('.')[0])
    ) {
      return false;
    }
    return true;
  });
}

export const useCheckConfiguration = (props: Namespace.UseCheckConfigurationProps) => {
  const { technology, configurationProfile, configurationLog, autoChecking } = props;

  const [detected, setDetected] = useState<Namespace.Detected[]>([]);
  const detectedKey = useMemo(() => {
    return detected
      .map((d) => d.equipment?.serial ?? '')
      .filter((s) => s.length > 0)
      .sort()
      .join('|');
  }, [detected]);
  const [isDetecting, setIsDetecting] = useState<boolean>(false);

  const [checked, setChecked] = useState<Namespace.Checked[]>([]);
  const [isChecking, setIsChecking] = useState<boolean>(false);

  const [autoCheckingEnabled, setAutoCheckingEnabled] = useState<boolean>(autoChecking ?? false);

  const toggleAutoChecking = (checked: boolean) => setAutoCheckingEnabled(checked);

  // hook that handle interactions with devicess
  const { ports, handleDetection, handleGetConfig, requestPort, isIdentified } =
    useTechnology(technology);

  // function that handle the check process, check if the process was successful and save result on database
  const check = async (
    detected: Namespace.Detected[],
    configurationProfile?: IConfigurationProfile | null,
    configurationLog?: IConfigurationLog[] | null,
  ) => {
    setIsChecking(true);

    try {
      // 1) Filtrar apenas dispositivos com serial e firmware válidos
      const validDevices = detected.filter(
        (
          d,
        ): d is typeof d & {
          equipment: { serial: string; firmware: string };
        } => Boolean(d.equipment?.serial && d.equipment?.firmware),
      );

      // 2) Puxar configuração atual de todos eles
      const results = await handleGetConfig(validDevices);

      // 3) Iterar, calcular checks e montar operações de bulk-update
      const bulkOps: Array<{
        query: { id: string };
        value: Partial<
          Pick<
            IConfigurationLog,
            | 'equipment'
            | 'checked'
            | 'checked_at'
            | 'applied_profile'
            | 'desired_profile'
            | 'id'
            | 'technology'
            | 'user'
            | 'status'
            | 'created_at'
          >
        >;
      }> = [];

      const finalLogs = results.map(({ port, equipment, config, messages }) => {
        const now = new Date();
        let checked = false;
        let configurationLogId: string | undefined;
        let configurationProfileId: string | undefined;

        // a) Se veio um configurationProfile, comparar com ele
        if (configurationProfile) {
          const rawDiffs = [
            ...diffObjects({
              desired: configurationProfile.config.general ?? {},
              applied: (config as any).general ?? {},
              keySelection: 'desired',
            }),
            ...diffObjects({
              desired: configurationProfile.config.specific ?? {},
              applied: (config as any).specific ?? {},
              keySelection: 'desired',
            }),
          ];
          // Ignora ruído de objeto vazio e campos write-only do BWS 4G com leitura vazia
          let diffs = filterIrrelevantDiffs(rawDiffs);
          // For NB2, filter out IP port differences when in DNS mode
          diffs = filterNb2DnsModeDiffs(diffs, configurationProfile.config.general ?? {});
          checked = diffs.length === 0;
          configurationProfileId = configurationProfile.id;
          const logId = crypto.randomUUID();
          bulkOps.push({
            query: { id: logId },
            value: {
              equipment: transformEquipment(equipment),
              checked,
              checked_at: now,
              desired_profile: {
                id: configurationProfile.id,
                name: configurationProfile.name,
                type: configurationProfile.type,
                config: configurationProfile.config as Record<string, unknown>,
              },
              applied_profile: config,
              id: logId,
              technology: {
                id: technology?.id ?? '',
                system_name: technology?.name.system ?? '',
              },
              status: true,
              created_at: new Date(),
            },
          });

          // b) Senão, se vier um configurationLog, tentar encontrar o log existente
        } else if (configurationLog) {
          const existing = configurationLog.find((l) => l.equipment.serial === equipment.serial);
          if (existing) {
            const rawDiffs = [
              ...diffObjects({
                desired: (existing?.applied_profile as any)?.general ?? {},
                applied: (config as any).general ?? {},
                keySelection: 'desired',
              }),
              ...diffObjects({
                desired: (existing?.applied_profile as any)?.specific ?? {},
                applied: (config as any).specific ?? {},
                keySelection: 'desired',
              }),
            ];
            // Ignora ruído de objeto vazio e campos write-only do BWS 4G com leitura vazia
            let diffs = filterIrrelevantDiffs(rawDiffs);
            // For NB2, filter out IP port differences when in DNS mode
            diffs = filterNb2DnsModeDiffs(diffs, (existing?.applied_profile as any)?.general ?? {});
            checked = diffs.length === 0;
            configurationLogId = existing.id;
            bulkOps.push({
              query: { id: configurationLogId },
              value: {
                equipment: transformEquipment(equipment),
                checked,
                checked_at: now,
                applied_profile: config,
              },
            });
          }
        }

        // d) Retorna o objeto final para setChecked
        return {
          port,
          equipment,
          messages,
          config,
          checked,
          checked_at: now,
          configurationLogId,
          configurationProfileId,
        };
      });

      // 4) Executa o bulk update e atualiza o state
      if (bulkOps.length) {
        const res = await fetchWithAuth('/api/production/configuration-logs/bulk', {
          method: 'PUT',
          body: JSON.stringify({ operations: bulkOps }),
        });
        if (!res.ok) throw new Error('Erro ao salvar os logs');
      }
      setChecked((prev) => [...prev, ...finalLogs]);
    } catch (error) {
      console.error('[ERROR] check use-configuration', error);
    } finally {
      setIsChecking(false);
    }
  };

  // useEffect used to identify devices when connected via serial ports
  useEffect(() => {
    const interval = setInterval(async () => {
      if (isChecking) return;

      if (!isDetecting && ports.length) {
        setIsDetecting(true);
        try {
          const detected = (await handleDetection(ports)).filter((d) => d.response?.serial);

          setDetected(() => {
            const map = new Map();

            for (const { port, response } of detected) {
              map.set(response?.serial, {
                port,
                equipment: response,
                status: !response ? 'not_identified' : isIdentified(response),
              });
            }

            return Array.from(new Set(map.values()));
          });
        } finally {
          setIsDetecting(false);
        }
      } else if (!isDetecting && !ports.length) {
        setDetected([]);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [ports, handleDetection, isChecking, isDetecting, isIdentified]);

  useEffect(() => {
    (async () => {
      if (detectedKey.length > 0 && autoCheckingEnabled) {
        await check(detected, configurationProfile, configurationLog);
      }
    })();
    // biome-ignore lint/correctness/useExhaustiveDependencies: check recreates on every render and would cause infinite loop
  }, [detectedKey, configurationProfile, configurationLog, autoCheckingEnabled, check, detected]);

  return {
    checked,
    detected,
    check,
    requestPort,
    isChecking,
    isDetecting,
    toggleAutoChecking,
    autoCheckingEnabled,
  };
};

export default useCheckConfiguration;
